class ExitIntentSurveyService {
    private lastDate: number = -1;

    private lastScrollTop: number = -1;

    private scrollSpeedModifier: number = 20;

    private inactivityTimeout: number | undefined;

    private visibilityChangeTimeout: number | undefined;

    private inactivityDelay: number = 20000;

    private minimumTrackingDelayInMs: number = 25;

    private showModalCallback: (() => void) | null = null;

    public initialize(showModalCallback: () => void, exitIntentSurveyDelay: number) {
        this.showModalCallback = showModalCallback;
        if (exitIntentSurveyDelay > 0) {
            this.inactivityDelay = exitIntentSurveyDelay * 1000;
        }
    }

    public startMonitoring() {
        if (this.isTouchDevice()) {
            window.addEventListener('scroll', this.detectFastScroll.bind(this));
            this.startInactivityTimer();
            document.addEventListener('touchstart', this.resetInactivityTimer.bind(this));
        }
        document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    public stopMonitoring() {
        window.removeEventListener('scroll', this.detectFastScroll.bind(this));
        document.removeEventListener('touchstart', this.resetInactivityTimer.bind(this));
        document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    private isTouchDevice(): boolean {
        return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    }

    private didScroll(timeStamp: number, scrollTop: number) {
        if (this.lastDate + this.minimumTrackingDelayInMs <= timeStamp) {
            const offset = Math.abs(scrollTop - this.lastScrollTop);
            const direction = this.getDirection(scrollTop);
            const delayInMs = timeStamp - this.lastDate;
            const speedInPxPerMs = offset / delayInMs;

            if (speedInPxPerMs > 0) {
                this.checkScrollSpeed(speedInPxPerMs, direction);
            }

            this.lastDate = timeStamp;
        }
    }

    private getDirection(scrollTop: number): string {
        const currentScrollTop = this.lastScrollTop;
        this.lastScrollTop = scrollTop;

        if (currentScrollTop > -1) {
            return currentScrollTop >= scrollTop ? 'down' : 'up';
        }

        return 'unknown';
    }

    private detectFastScroll(e: Event) {
        const scrollTop = window.pageYOffset || document.body.scrollTop;
        this.didScroll(e.timeStamp as number, scrollTop);
    }

    private checkScrollSpeed(speedInPxPerMs: number, newDirection: string) {
        if (newDirection === 'down' && speedInPxPerMs > this.scrollSpeedModifier) {
            this.showContent();
        }
    }

    private startInactivityTimer() {
        this.inactivityTimeout = window.setTimeout(() => {
            this.showContent();
        }, this.inactivityDelay);
    }

    private resetInactivityTimer() {
        clearTimeout(this.inactivityTimeout);
        this.startInactivityTimer();
    }

    private handleVisibilityChange() {
        if (document.hidden) {
            this.visibilityChangeTimeout = window.setTimeout(() => {
                this.showContent();
            }, this.inactivityDelay);
        } else {
            clearTimeout(this.visibilityChangeTimeout);
        }
    }

    private showContent() {
        if (this.showModalCallback) {
            this.showModalCallback();
        }
    }

    private get apiUrl(): string {
        return 'https://hooks.zapier.com/hooks/catch/17086219/23p3mc7/';
    }

    public async sendSurveyResponse({
        correlationId,
        dateTime,
        pageUrl,
        stepId,
        manufacturerName,
        manufacturerId,
        rangeName,
        rangeId,
        device,
        browser,
        userAgent,
        ipAddress,
        surveyResponse
    }) {
        const requestBody = {
            CorrelationId: correlationId,
            DateTime: dateTime,
            Event: 'SurveyResponse',
            PageUrl: pageUrl,
            StepId: stepId,
            ManufacturerName: manufacturerName,
            ManufacturerId: manufacturerId,
            RangeName: rangeName,
            RangeId: rangeId,
            Device: device,
            Browser: browser,
            UserAgent: userAgent,
            IpAddress: ipAddress,
            SurveyResponse: surveyResponse
        };

        try {
            const response = await fetch(this.apiUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}`);
            }
        } catch (error) {
            console.error('Error sending survey response:', error);
        }
    }
}

// eslint-disable-next-line import/prefer-default-export
export const exitIntentSurveyService = new ExitIntentSurveyService();
